import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Outlet } from 'react-router-dom';
import { getCurrentUser } from '../services/authService';
import { isExternalUser } from '../utils/userUtils';
import { AUTH_PERMISSIONS_STALE_EVENT } from '../constants/authEvents';

const AuthContext = createContext(null);

const REFRESH_COOLDOWN_MS = 30000;

/**
 * Provides centralized auth state to all descendants.
 *
 * - Fetches user + privileges ONCE on mount via getCurrentUser().
 * - Shares user, allPrivileges, isSuper, isExternal to the entire tree.
 * - Listens for AUTH_PERMISSIONS_STALE_EVENT to re-fetch (invalidation).
 * - Cooldown prevents rapid consecutive refreshes (e.g. multiple 403s).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [allPrivileges, setAllPrivileges] = useState([]);
  const [isSuper, setIsSuper] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const lastRefreshRef = useRef(0);

  const fetchUserData = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      if (response) {
        const userData = response.user || null;
        setUser(userData);
        setAllPrivileges(response.allPrivileges || []);
        setIsSuper(userData?.is_super === 1);
        setIsExternal(isExternalUser(userData));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const refreshPermissions = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) return;
    lastRefreshRef.current = now;
    await fetchUserData();
  }, [fetchUserData]);

  // Listen for invalidation events dispatched by the axios interceptor
  useEffect(() => {
    const handleStale = () => refreshPermissions();
    window.addEventListener(AUTH_PERMISSIONS_STALE_EVENT, handleStale);
    return () =>
      window.removeEventListener(AUTH_PERMISSIONS_STALE_EVENT, handleStale);
  }, [refreshPermissions]);

  /**
   * Called by the login form after successful authentication.
   * Returns a promise so the caller can await fresh data before navigating.
   */
  const refreshAfterLogin = useCallback(async () => {
    setIsLoading(true);
    await fetchUserData();
  }, [fetchUserData]);

  const value = useMemo(
    () => ({
      user,
      allPrivileges,
      isSuper,
      isExternal,
      isLoading,
      isAuthenticated,
      refreshPermissions,
      refreshAfterLogin,
    }),
    [
      user,
      allPrivileges,
      isSuper,
      isExternal,
      isLoading,
      isAuthenticated,
      refreshPermissions,
      refreshAfterLogin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Route-element wrapper that provides AuthContext and renders <Outlet />.
 * Use as: <Route element={<AuthProviderOutlet />}>
 */
export const AuthProviderOutlet = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

/**
 * Hook to consume AuthContext. Must be used within an AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
